import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as entriesApi from "@api/entries.api";
import type { Entry } from "@/types";

interface EntriesState {
  items: Entry[];
  loading: boolean;
}

const initialState: EntriesState = {
  items: [],
  loading: false,
};

export const fetchEntries = createAsyncThunk(
  "entries/fetchAll",
  async (filters?: {
    categoryId?: string;
    subcategoryId?: string;
    from?: string;
    to?: string;
  }) => {
    return await entriesApi.getEntries(filters);
  },
);

export const createEntry = createAsyncThunk(
  "entries/create",
  async (data: {
    name?: string | null;
    description?: string;
    categoryId: string;
    subcategoryId?: string | null;
    amount: number;
    currency?: string;
    spentAt?: string;
  }) => {
    return await entriesApi.createEntry(data);
  },
);

export const updateEntry = createAsyncThunk(
  "entries/update",
  async ({
    id,
    data,
  }: {
    id: string;
    data: {
      name?: string | null;
      description?: string;
      categoryId?: string;
      subcategoryId?: string | null;
      amount?: number;
      currency?: string;
      spentAt?: string;
    };
  }) => {
    return await entriesApi.updateEntry(id, data);
  },
);

export const removeEntry = createAsyncThunk(
  "entries/remove",
  async (id: string) => {
    await entriesApi.deleteEntry(id);
    return id;
  },
);

const entriesSlice = createSlice({
  name: "entries",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEntries.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEntries.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchEntries.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createEntry.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      .addCase(updateEntry.fulfilled, (state, action) => {
        const idx = state.items.findIndex((e) => e.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(removeEntry.fulfilled, (state, action) => {
        state.items = state.items.filter((e) => e.id !== action.payload);
      });
  },
});

export default entriesSlice.reducer;
